import React, { useState } from 'react';
import { X, ChevronDown, ChevronUp, Filter, Search } from 'lucide-react'; //

const FilterSection = ({ title, children, isOpen, onToggle }) => (
  <div className="border-b border-gray-100 last:border-0">
    <button
      onClick={onToggle}
      className="flex items-center justify-between w-full py-3 px-4 text-left hover:bg-gray-50 transition-colors"
    >
      <span className="font-medium text-gray-700 text-sm">{title}</span>
      {isOpen ? (
        <ChevronUp className="h-4 w-4 text-gray-400" />
      ) : (
        <ChevronDown className="h-4 w-4 text-gray-400" />
      )}
    </button>
    {isOpen && <div className="p-4 pt-0">{children}</div>}
  </div>
);

const CheckboxGroup = ({ options, selected, onChange }) => (
  <div className="space-y-2">
    {options.map((option) => (
      <label key={option} className="flex items-start space-x-2 cursor-pointer">
        <input
          type="checkbox"
          checked={selected?.includes(option)}
          onChange={(e) => {
            const newSelected = e.target.checked
              ? [...(selected || []), option]
              : (selected || []).filter((item) => item !== option);
            onChange(newSelected);
          }}
          className="mt-1 w-4 h-4 text-black rounded border-gray-300 focus:ring-gray-500"
        />
        <span className="text-sm text-gray-600 capitalize break-words">
          {option.replace(/_/g, ' ')}
        </span>
      </label>
    ))}
  </div>
);

const RangeSlider = ({ min, max, value, onChange, label }) => {
  // Ensure we have valid numbers
  const safeMin = Number(min) || 0;
  const safeMax = Number(max) || 100;
  const [localMin, setLocalMin] = useState(value?.min_score ?? safeMin);
  const [localMax, setLocalMax] = useState(value?.max_score ?? safeMax);

  const handleMinChange = (e) => {
    const val = Math.min(Number(e.target.value), localMax - 1);
    setLocalMin(val);
    onChange({ min_score: val, max_score: localMax });
  };

  const handleMaxChange = (e) => {
    const val = Math.max(Number(e.target.value), localMin + 1);
    setLocalMax(val);
    onChange({ min_score: localMin, max_score: val });
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between text-xs text-gray-500">
        <span>Min: {localMin}</span>
        <span>Max: {localMax}</span>
      </div>
      <div className="relative h-2 bg-gray-200 rounded-full">
        <input
          type="range"
          min={safeMin}
          max={safeMax}
          value={localMin}
          onChange={handleMinChange}
          className="absolute w-full h-full opacity-0 cursor-pointer z-10"
        />
        <input
          type="range"
          min={safeMin}
          max={safeMax}
          value={localMax}
          onChange={handleMaxChange}
          className="absolute w-full h-full opacity-0 cursor-pointer z-10"
        />
        <div
          className="absolute h-full bg-black rounded-full"
          style={{
            left: `${((localMin - safeMin) / (safeMax - safeMin)) * 100}%`,
            right: `${100 - ((localMax - safeMin) / (safeMax - safeMin)) * 100}%`,
          }}
        />
        {/* Thumbs visual representation */}
        <div
          className="absolute h-4 w-4 bg-white border-2 border-black rounded-full top-1/2 -translate-y-1/2 shadow transform hover:scale-110 transition-transform"
          style={{ left: `${((localMin - safeMin) / (safeMax - safeMin)) * 100}%` }}
        />
        <div
          className="absolute h-4 w-4 bg-white border-2 border-black rounded-full top-1/2 -translate-y-1/2 shadow transform hover:scale-110 transition-transform"
          style={{ left: `${((localMax - safeMin) / (safeMax - safeMin)) * 100}%`, transform: 'translate(-100%, -50%)' }}
        />
      </div>
    </div>
  );
};

const BooleanFilter = ({ value, onChange, label }) => (
  <div className="flex items-center space-x-4">
    <label className="flex items-center space-x-2 cursor-pointer">
      <input
        type="radio"
        name={label}
        checked={value === true}
        onChange={() => onChange(true)}
        className="text-black focus:ring-gray-500"
      />
      <span className="text-sm text-gray-600">Yes</span>
    </label>
    <label className="flex items-center space-x-2 cursor-pointer">
      <input
        type="radio"
        name={label}
        checked={value === false}
        onChange={() => onChange(false)}
        className="text-black focus:ring-gray-500"
      />
      <span className="text-sm text-gray-600">No</span>
    </label>
    <label className="flex items-center space-x-2 cursor-pointer">
      <input
        type="radio"
        name={label}
        checked={value === undefined || value === null}
        onChange={() => onChange(undefined)}
        className="text-black focus:ring-gray-500"
      />
      <span className="text-sm text-gray-600">Any</span>
    </label>
  </div>
);

export const FilterContent = ({
  filterOptions,
  filters,
  onUpdateFilter,
  openSections,
  onToggleSection,
  status = true,
  search = true
}) => {
  return (
    <div className="flex-1 overflow-y-auto">
      {filterOptions && (
        <>
          {/* Search Query */}
          {
            search ? (
              <div className="p-4 border-b border-gray-100">
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                  Search
                </label>
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search queries..."
                    value={filters.search_query || ''}
                    onChange={(e) => onUpdateFilter('search_query', null, e.target.value)}
                    className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-gray-500 focus:border-gray-500 transition-colors"
                  />
                  <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                </div>
              </div>
            ) : (
              <></>
            )
          }

          {/* Status */}
          {
            status ? (
              <FilterSection
                title="Status"
                isOpen={openSections.status}
                onToggle={() => onToggleSection('status')}
              >
                <CheckboxGroup
                  options={filterOptions.status || []}
                  selected={filters.status}
                  onChange={(val) => onUpdateFilter('status', null, val)}
                />
              </FilterSection>
            ) : (
              <></>
            )
          }

          {/* External Integration */}
          {filterOptions.external_integration && filterOptions.external_integration.length > 0 && (
            <FilterSection
              title="Source"
              isOpen={openSections.external_integration}
              onToggle={() => onToggleSection('external_integration')}
            >
              <CheckboxGroup
                options={filterOptions.external_integration}
                selected={filters.external_integration}
                onChange={(val) => onUpdateFilter('external_integration', null, val)}
              />
            </FilterSection>
          )}

          {/* Classification */}
          <FilterSection
            title="Classification"
            isOpen={openSections.classification}
            onToggle={() => onToggleSection('classification')}
          >
            <div className="space-y-4">
              <div>
                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Primary Category</h4>
                <CheckboxGroup
                  options={filterOptions.classification?.primary_category || []}
                  selected={filters.classification?.primary_category}
                  onChange={(val) => onUpdateFilter('classification', 'primary_category', val)}
                />
              </div>
              <div>
                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Intent</h4>
                <CheckboxGroup
                  options={filterOptions.classification?.intent || []}
                  selected={filters.classification?.intent}
                  onChange={(val) => onUpdateFilter('classification', 'intent', val)}
                />
              </div>
            </div>
          </FilterSection>

          {/* Sentiment */}
          <FilterSection
            title="Sentiment"
            isOpen={openSections.sentiment}
            onToggle={() => onToggleSection('sentiment')}
          >
            <div className="space-y-4">
              <div>
                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Emotion</h4>
                <CheckboxGroup
                  options={filterOptions.sentiment?.emotion || []}
                  selected={filters.sentiment?.emotion}
                  onChange={(val) => onUpdateFilter('sentiment', 'emotion', val)}
                />
              </div>
              <div>
                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Overall</h4>
                <CheckboxGroup
                  options={filterOptions.sentiment?.overall || []}
                  selected={filters.sentiment?.overall}
                  onChange={(val) => onUpdateFilter('sentiment', 'overall', val)}
                />
              </div>
              <div>
                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Score Range</h4>
                <RangeSlider
                  min={filterOptions.sentiment?.min_score?.[0]}
                  max={filterOptions.sentiment?.max_score?.[1]}
                  value={{ min_score: filters.sentiment?.min_score, max_score: filters.sentiment?.max_score }}
                  onChange={(val) => {
                    // Note: Logic to update filter object structure is expected in onUpdateFilter or wrapper
                    // But here we need to pass a value.
                    // The parent's updateFilter expects (category, subCategory, value)
                    // For range slider, we need to update two fields.
                    // Since onUpdateFilter is simple (cat, sub, val), we might need a custom handler here OR
                    // adjust onUpdateFilter in parent to handle object merge if subCategory is null?
                    // Wait, the original code did:
                    /*
                    onChange={(val) => {
                     const newFilters = { ...localFilters };
                     if (!newFilters.sentiment) newFilters.sentiment = {};
                     newFilters.sentiment.min_score = val.min_score;
                     newFilters.sentiment.max_score = val.max_score;
                     setLocalFilters(newFilters);
                  }}
                    */
                    // So we should expose a way to do this.
                    // Let's call onUpdateFilter with a special convention or just handle it here if onUpdateFilter allows full object update?
                    // No, onUpdateFilter signature is fixed.
                    // I'll assume onUpdateFilter can handle 'sentiment' with an object update if I pass it, 
                    // OR I should use a more flexible update callback.
                    // Let's pass 'sentiment' as category, and null as subcategory, and the WHOLE sentiment object as value?
                    // No, that overwrites.

                    // I will invoke onUpdateFilter twice.
                    onUpdateFilter('sentiment', 'min_score', val.min_score);
                    onUpdateFilter('sentiment', 'max_score', val.max_score);
                  }}
                />
              </div>
            </div>
          </FilterSection>

          {/* Urgency */}
          <FilterSection
            title="Urgency"
            isOpen={openSections.urgency}
            onToggle={() => onToggleSection('urgency')}
          >
            <div className="space-y-4">
              <div>
                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Level</h4>
                <CheckboxGroup
                  options={filterOptions.urgency?.level || []}
                  selected={filters.urgency?.level}
                  onChange={(val) => onUpdateFilter('urgency', 'level', val)}
                />
              </div>
              <div>
                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Business Impact</h4>
                <CheckboxGroup
                  options={filterOptions.urgency?.business_impact || []}
                  selected={filters.urgency?.business_impact}
                  onChange={(val) => onUpdateFilter('urgency', 'business_impact', val)}
                />
              </div>
            </div>
          </FilterSection>

          {/* Root Cause */}
          <FilterSection
            title="Root Cause"
            isOpen={openSections.root_cause}
            onToggle={() => onToggleSection('root_cause')}
          >
            <CheckboxGroup
              options={filterOptions.root_cause?.category || []}
              selected={filters.root_cause?.category}
              onChange={(val) => onUpdateFilter('root_cause', 'category', val)}
            />
          </FilterSection>

          {/* RAG Performance */}
          <FilterSection
            title="RAG Performance"
            isOpen={openSections.rag_performance}
            onToggle={() => onToggleSection('rag_performance')}
          >
            <div className="space-y-4">
              <div>
                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Context Quality</h4>
                <CheckboxGroup
                  options={filterOptions.rag_performance?.context_quality || []}
                  selected={filters.rag_performance?.context_quality}
                  onChange={(val) => onUpdateFilter('rag_performance', 'context_quality', val)}
                />
              </div>
              <div>
                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Knowledge Gap Detected</h4>
                <BooleanFilter
                  value={filters.rag_performance?.knowledge_gap_detected}
                  onChange={(val) => onUpdateFilter('rag_performance', 'knowledge_gap_detected', val)}
                />
              </div>
            </div>
          </FilterSection>

          {/* AI Response Quality */}
          <FilterSection
            title="AI Response Quality"
            isOpen={openSections.ai_response_quality}
            onToggle={() => onToggleSection('ai_response_quality')}
          >
            <div className="space-y-4">
              <div>
                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Potential Hallucination</h4>
                <BooleanFilter
                  value={filters.ai_response_quality?.potential_hallucination}
                  onChange={(val) => onUpdateFilter('ai_response_quality', 'potential_hallucination', val)}
                />
              </div>
            </div>
          </FilterSection>

        </>
      )}
    </div>
  );
};

const FilterSidebar = ({ isOpen, onClose, filterOptions, filters, onFilterChange, onApply, onClear }) => {
  const [localFilters, setLocalFilters] = useState(filters);
  const [openSections, setOpenSections] = useState({
    status: true,
    external_integration: true,
    classification: true,
  });

  // Sync local state when prop changes (e.g. when cleared from parent)
  React.useEffect(() => {
    setLocalFilters(filters);
  }, [filters, isOpen]);

  const toggleSection = (section) => {
    setOpenSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  const updateFilter = (category, subCategory, value) => {
    const newFilters = { ...localFilters };
    if (!newFilters[category]) newFilters[category] = {};

    if (subCategory) {
      if (value === undefined || (Array.isArray(value) && value.length === 0)) {
        delete newFilters[category][subCategory];
        if (Object.keys(newFilters[category]).length === 0) delete newFilters[category];
      } else {
        newFilters[category][subCategory] = value;
      }
    } else {
      if (value === undefined || (Array.isArray(value) && value.length === 0)) {
        delete newFilters[category];
      } else {
        newFilters[category] = value;
      }
    }
    setLocalFilters(newFilters);
  };

  const handleApply = () => {
    onFilterChange(localFilters);
    onApply();
  };

  const handleClear = () => {
    setLocalFilters({});
    onClear();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-y-0 right-0 w-80 bg-white shadow-2xl z-50 overflow-hidden flex flex-col border-l border-gray-200 transform transition-transform duration-300 ease-in-out">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gray-50">
        <div className="flex items-center space-x-2">
          <Filter className="h-5 w-5 text-black" />
          <h3 className="font-semibold text-gray-900">Filters</h3>
        </div>
        <button
          onClick={onClose}
          className="p-1 hover:bg-gray-200 rounded-full transition-colors text-gray-500"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Filter Content */}
      <FilterContent
        filterOptions={filterOptions}
        filters={localFilters}
        onUpdateFilter={updateFilter}
        openSections={openSections}
        onToggleSection={toggleSection}
      />

      {/* Footer Actions */}
      <div className="p-4 border-t border-gray-200 bg-gray-50 flex space-x-3">
        <button
          onClick={handleClear}
          className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
        >
          Clear All
        </button>
        <button
          onClick={handleApply}
          className="flex-1 px-4 py-2 text-sm font-medium text-white bg-black rounded-lg hover:bg-gray-800 transition-colors shadow-sm"
        >
          Apply Filters
        </button>
      </div>
    </div>
  );
};

export default FilterSidebar;
